import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/context/i18n";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { WatchButton } from "@/components/WatchButton";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Loader2, Sparkles, Bell, Star, CalendarDays, Users, SlidersHorizontal, X, Check } from "lucide-react";

const Stars = ({ n }) => (
  <span className="flex items-center gap-0.5" aria-label={`${n} stars`}>
    {Array.from({ length: n || 0 }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 text-[#C9A227] fill-[#C9A227]" />)}
  </span>
);

const scoreOf = (h) => Math.min(9.8, 6.8 + (h.stars || 3) * 0.55).toFixed(1);

export default function HotelResults() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { formatPrice } = useApp();
  const { t } = useI18n();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [aiFilters, setAiFilters] = useState(null);
  const [minStars, setMinStars] = useState(0);
  const [maxPrice, setMaxPrice] = useState(1000);
  const [sort, setSort] = useState("recommended");
  const [onlyBookable, setOnlyBookable] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [destName, setDestName] = useState("");

  const aiQuery = params.get("ai");
  const dflt = { ci: new Date(Date.now() + 21 * 864e5).toISOString().slice(0, 10), co: new Date(Date.now() + 24 * 864e5).toISOString().slice(0, 10) };
  const search = {
    destination: params.get("destination") || "IST",
    checkIn: params.get("checkIn") || dflt.ci, checkOut: params.get("checkOut") || dflt.co,
    adults: +(params.get("adults") || 2), children: +(params.get("children") || 0), rooms: +(params.get("rooms") || 1),
  };
  const nights = Math.max(1, Math.round((new Date(search.checkOut) - new Date(search.checkIn)) / 864e5));

  useEffect(() => {
    api.get("/destinations").then((r) => {
      const d = (r.data || []).find((x) => x.code === (params.get("destination") || "IST"));
      setDestName(d ? d.city : "");
    }).catch(() => {});
    // eslint-disable-next-line
  }, [params]);

  useEffect(() => {
    setLoading(true); setError(""); setAiFilters(null);
    const req = aiQuery
      ? api.post("/ai/search", { query: aiQuery })
      : api.post("/hotels/search", { ...search, source: "auto" });
    req.then((r) => {
      setHotels(r.data.hotels || []);
      setError(r.data.error || "");
      if (r.data.filters) setAiFilters(r.data.filters);
    }).catch((e) => setError(e.response?.data?.detail || "Search failed")).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [params]);

  const filtered = hotels
    .filter((h) => (h.stars || 0) >= minStars && (h.minRate == null || h.minRate <= maxPrice))
    .filter((h) => (onlyBookable ? h.minRate != null : true));

  const sorted = [...filtered].sort((a, b) => {
    if (sort === "price_low") return (a.minRate ?? 1e9) - (b.minRate ?? 1e9);
    if (sort === "price_high") return (b.minRate ?? -1) - (a.minRate ?? -1);
    if (sort === "stars") return (b.stars || 0) - (a.stars || 0);
    return (a.minRate == null) - (b.minRate == null);
  });

  const openHotel = (h) => navigate(
    `/hotels/${h.code}?checkIn=${search.checkIn}&checkOut=${search.checkOut}&adults=${search.adults}&children=${search.children}&rooms=${search.rooms}&source=${h.src || "hbx"}`,
    { state: { hotel: h } }
  );

  const Filters = () => (
    <div className="space-y-7">
      <div>
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#0B2038] mb-3">{t("max_price")}</p>
        <p className="text-sm font-bold text-[#009E9E] mb-2">{formatPrice(maxPrice)}</p>
        <Slider data-testid="filter-price" value={[maxPrice]} min={50} max={1000} step={25} onValueChange={(v) => setMaxPrice(v[0])} />
      </div>
      <div>
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#0B2038] mb-3">{t("min_stars")}</p>
        <div className="flex flex-wrap gap-2">
          {[0, 3, 4, 5].map((s) => (
            <button key={s} data-testid={`filter-stars-${s}`} onClick={() => setMinStars(s)}
              className={`px-3 h-9 rounded-xl text-xs font-bold border transition-colors ${minStars === s ? "bg-[#0B2038] text-white border-[#0B2038]" : "border-[#E2E8F0] text-[#475569] hover:border-[#0B2038]"}`}>
              {s === 0 ? t("any") : `${s}★+`}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#0B2038] mb-3">{t("availability")}</p>
        <button data-testid="filter-bookable" onClick={() => setOnlyBookable(!onlyBookable)}
          className="flex items-center gap-2.5 text-sm font-semibold text-[#0B2038]">
          <span className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${onlyBookable ? "bg-[#009E9E] border-[#009E9E]" : "border-[#E2E8F0] bg-white"}`}>
            {onlyBookable && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
          </span>
          {t("only_bookable")}
        </button>
      </div>
    </div>
  );

  const Card = ({ h }) => {
    const bookable = h.minRate != null;
    const watchPayload = {
      hotelCode: h.code, hotelName: h.name, image: h.images?.[0],
      destination: search.destination, checkIn: search.checkIn, checkOut: search.checkOut,
      adults: search.adults, children: search.children, rooms: search.rooms,
      source: h.src || "hbx", currentPriceEur: h.minRate ?? null,
    };
    return (
      <article data-testid={`hotel-card-${h.code}`}
        className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden flex flex-col sm:flex-row card-lift">
        <div className="sm:w-[300px] h-52 sm:h-auto shrink-0 bg-[#E2E8F0] relative">
          {h.images?.[0]
            ? <img src={h.images[0]} alt={h.name} className={`w-full h-full object-cover ${bookable ? "" : "grayscale opacity-90"}`} />
            : <div className="w-full h-full flex items-center justify-center text-[#94A3B8] text-xs">Vaya</div>}
          {!bookable && <span className="absolute top-3 left-3 text-[10px] font-extrabold uppercase tracking-wide bg-white/95 text-[#475569] px-2.5 py-1 rounded-full">{t("not_bookable")}</span>}
        </div>

        <div className="flex-1 p-5 sm:p-6 flex flex-col min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Stars n={h.stars} />
              <h3 className="font-display text-lg sm:text-xl font-extrabold text-[#0B2038] leading-tight mt-1.5 line-clamp-2">{h.name}</h3>
              <p className="text-[#475569] text-xs flex items-center gap-1 mt-1.5">
                <MapPin className="w-3.5 h-3.5 text-[#009E9E]" />{h.zoneName ? `${h.zoneName}, ` : ""}{h.destinationName}
              </p>
            </div>
            <div className="shrink-0 text-center bg-[#0B2038] text-white rounded-xl rounded-tr-none px-2.5 py-1.5">
              <p className="font-display text-base font-extrabold leading-none">{scoreOf(h)}</p>
              <p className="text-[9px] uppercase tracking-wider text-white/60 mt-0.5">{t("score")}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 mt-3">
            {(h.facilities || []).slice(0, 3).map((f, i) => (
              <span key={i} className="text-[11px] font-semibold text-[#475569] bg-[#F8FAFC] border border-[#E2E8F0] rounded-full px-2.5 py-1">{f}</span>
            ))}
            {bookable && <span className="text-[11px] font-bold text-[#009E9E] bg-[#009E9E]/10 rounded-full px-2.5 py-1">{t("free_cancel")}</span>}
          </div>

          <div className="mt-auto flex items-end justify-between gap-3 pt-5 border-t border-[#E2E8F0] mt-5">
            <div>
              {bookable ? (
                <>
                  <p className="text-[11px] text-[#475569] font-semibold">{t("from")}</p>
                  <p className="font-display text-2xl font-extrabold text-[#0B2038] leading-none" data-testid={`hotel-price-${h.code}`}>
                    {formatPrice(h.minRate / nights)}
                  </p>
                  <p className="text-[11px] text-[#475569] mt-1">{t("per_night")} · {formatPrice(h.minRate)} {t("total_stay")}</p>
                </>
              ) : (
                <p className="text-sm font-bold text-[#475569]" data-testid={`hotel-norate-${h.code}`}>{t("price_on_request")}</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <WatchButton compact payload={watchPayload}
                className="h-11 w-11 justify-center rounded-xl border border-[#E2E8F0] text-[#0B2038] hover:border-[#0B2038] hover:text-[#009E9E]" />
              {bookable ? (
                <button data-testid={`hotel-view-${h.code}`} onClick={() => openHotel(h)}
                  className="rounded-xl bg-[#009E9E] hover:bg-[#007A7A] text-white h-11 px-6 text-sm font-bold transition-colors">
                  {t("book_now")}
                </button>
              ) : (
                <WatchButton payload={watchPayload}
                  className="rounded-xl border border-[#0B2038] text-[#0B2038] hover:bg-[#0B2038] hover:text-white h-11 px-5 text-sm" />
              )}
            </div>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="hotel-results-page">
      <Header />

      {/* search summary */}
      <div className="pt-[68px]">
        <div className="bg-[#0B2038] text-white">
          <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-5 flex flex-wrap items-center gap-x-6 gap-y-2">
            <h1 className="font-display text-xl sm:text-2xl font-extrabold" data-testid="results-heading">
              {loading ? t("searching") : `${sorted.length} ${t("stays_available")}`}
            </h1>
            {!aiQuery && (
              <>
                <span className="text-sm text-white/75 flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#C9A227]" />{destName || search.destination}</span>
                <span className="text-sm text-white/75 flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-[#C9A227]" />{search.checkIn} → {search.checkOut} · {nights} {t("nights")}</span>
                <span className="text-sm text-white/75 flex items-center gap-1.5"><Users className="w-4 h-4 text-[#C9A227]" />{search.adults} · {search.rooms} {t("rooms")}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-7 grid lg:grid-cols-[280px_1fr] gap-8">
        {/* filters sidebar */}
        <aside className="hidden lg:block" data-testid="hotel-filters">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 sticky top-[92px]">
            <p className="font-display text-lg font-extrabold text-[#0B2038] mb-5">{t("filters")}</p>
            <Filters />
          </div>
        </aside>

        <div className="min-w-0">
          {aiQuery && (
            <div className="mb-5 flex items-start gap-2.5 bg-white border border-[#E2E8F0] rounded-2xl p-4">
              <Sparkles className="w-4 h-4 text-[#C9A227] mt-0.5 shrink-0" />
              <div className="text-sm text-[#0B2038]">
                <span className="font-bold">"{aiQuery}"</span>
                {aiFilters && <span className="text-[#475569]"> · {t("parsed_as")}: {aiFilters.destination}{aiFilters.minStars ? ` · ${aiFilters.minStars}★+` : ""}{aiFilters.maxPriceEur ? ` · ≤ €${aiFilters.maxPriceEur}` : ""}</span>}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 mb-5">
            <button data-testid="mobile-filter-toggle" onClick={() => setShowFilters(true)}
              className="lg:hidden flex items-center gap-2 h-11 px-4 rounded-xl bg-white border border-[#E2E8F0] text-sm font-bold text-[#0B2038]">
              <SlidersHorizontal className="w-4 h-4" />{t("filters")}
            </button>
            <div className="ml-auto flex items-center gap-2">
              <span className="hidden sm:block text-xs font-bold uppercase tracking-wider text-[#475569]">{t("sort_by")}</span>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="sort-select" className="w-[190px] h-11 rounded-xl border-[#E2E8F0] bg-white text-sm font-semibold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white z-[600]">
                  <SelectItem value="recommended" data-testid="sort-recommended">{t("sort_recommended")}</SelectItem>
                  <SelectItem value="price_low" data-testid="sort-price-low">{t("sort_price_low")}</SelectItem>
                  <SelectItem value="price_high" data-testid="sort-price-high">{t("sort_price_high")}</SelectItem>
                  <SelectItem value="stars" data-testid="sort-stars">{t("sort_stars")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading && <div className="flex flex-col items-center py-24 text-[#475569]"><Loader2 className="w-8 h-8 animate-spin mb-3 text-[#009E9E]" />{t("searching")}</div>}
          {error && <div className="bg-[#FFFBEB] border border-amber-200 rounded-2xl p-4 text-amber-800 text-sm mb-5" data-testid="hotel-error">{error}</div>}
          {!loading && sorted.length === 0 && !error && <div className="bg-white border border-[#E2E8F0] rounded-2xl p-12 text-center text-[#475569]">{t("no_results")}</div>}
          <div className="space-y-5">{sorted.map((h) => <Card key={`${h.src || "x"}-${h.code}`} h={h} />)}</div>
        </div>
      </div>

      {/* mobile filter sheet */}
      {showFilters && (
        <div className="lg:hidden fixed inset-0 z-[550] bg-[#0B2038]/50 flex items-end" onClick={() => setShowFilters(false)}>
          <div className="bg-white w-full rounded-t-3xl p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <p className="font-display text-lg font-extrabold text-[#0B2038]">{t("filters")}</p>
              <button data-testid="close-filters" onClick={() => setShowFilters(false)} className="w-9 h-9 rounded-full bg-[#F8FAFC] flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <Filters />
            <button data-testid="apply-filters" onClick={() => setShowFilters(false)}
              className="mt-7 w-full h-12 rounded-xl bg-[#009E9E] text-white font-bold">{t("show_results")}</button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
